import { useEffect, useState } from "react";
import classNames from "classnames";
import CustomPagination from "shared/components/common/customPagination";
import CustomTable from "shared/components/common/customTable";
import { toastMessage } from "shared/components/common/toast";
import SkeletonLoader from "shared/loaders/skeleton";
import styles from "./style.module.scss";
import {
  FlagUserService,
  GetAllUsersService,
  SuspendUserService,
  UnFlagUserService,
  UnSuspendUserService,
} from "shared/services/users";
import { Icons, Images } from "assets";
import CustomToolTip from "shared/components/common/customToolTip";
import ConfirmationModal from "shared/modal/confirmation";
import { handleErrors } from "shared/utils/helper";
import FlagUserModal from "shared/modal/flagUser";

interface UsersData {
  id: number;
  profile_image: {
    fullpath: string;
  };
  name: string;
  email: string;
  address: string;
  status: string;
  is_suspended: boolean;
  flagged: boolean;
  city: string;
  state: string;
  username: string;
  phonenumber: string;
}
const DashboardUsers = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [users, setUsers] = useState<UsersData[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [selectedUser, setSelectedUser] = useState<UsersData>();
  const [suspendLoading, setSuspendLoading] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [showFlagModal, setShowFlagModal] = useState<boolean>(false);

  const handleShowFlagModal = () => {
    setShowFlagModal(true);
  };

  const handleCloseFlagModal = () => {
    setShowFlagModal(false);
  };

  const handleShowConfirmationModal = () => {
    setShowConfirmationModal(true);
  };

  const handleCloseConfirmationModal = () => {
    setShowConfirmationModal(false);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const tableHeads = [
    { title: "Profile Image", key: "img" },
    { title: "Name", key: "name" },
    { title: 'Username', key: 'username' },
    { title: "Email", key: "email" },
    { title: "Address", key: "address" },
    { title: 'Phone', key: 'phonenumber' },
    { title: "Status", key: "status" },
    { title: "Action", key: "action" },
  ];

  const handleGetAllUsers = async (page = 1) => {
    try {
      setLoading(true);
      const response = await GetAllUsersService(page, 10);
      const fetchedUsers = response.data.data.data || [];
      setUsers(fetchedUsers);
      setUsers(response.data.data.data || []);

      setTotalItems(response.data.data.meta.totalItems || 0);
    } catch (error) {
      console.error("Error fetching users:", error);
      toastMessage("error", "Failed to fetch users.");
    } finally {
      setLoading(false);
    }
  };

  const handleSuspendUser = async () => {
    if (!selectedUser) return;

    setSuspendLoading(true);
    if (selectedUser?.is_suspended) {
      UnSuspendUserService(selectedUser.id)
        .then(({ data, status }) => {
          if (status) {
            toastMessage("success", "User Activated Successfully");
            handleGetAllUsers(currentPage);
            handleCloseConfirmationModal();
          }
        })
        .catch((error) => {
          handleErrors(error);
        })
        .finally(() => {
          setSuspendLoading(false);
        });
    } else {
      SuspendUserService(selectedUser.id)
        .then(({ data, status }) => {
          if (status) {
            toastMessage("success", "User Suspended Successfully");
            handleGetAllUsers(currentPage);
            handleCloseConfirmationModal();
          }
        })
        .catch((error) => {
          handleErrors(error);
        })
        .finally(() => {
          setSuspendLoading(false);
        });
    }
  };

  useEffect(() => {
    handleGetAllUsers(currentPage);
  }, [currentPage]);

  return (
    <div className={styles.sectionContainer}>
      <div className={classNames(styles.tableWrapper, "relative")}>
        <CustomTable
          heads={tableHeads}
          title="Users"
          isEmpty={!loading && !users.length}
        >
          {loading
            ? Array.from({ length: 2 }).map((_, index) => (
                <TableRowLoader key={index} />
              ))
            : users.map((data, idx) => (
                <tr key={data.id}>
                  <td>
                    <img
                      src={data.profile_image?.fullpath || Images.DefaultAvatar}
                      alt=""
                    />
                  </td>
                  <td>{data.name}</td>
                  <td>{data.username}</td>
                  <td>{data.email}</td>
                  <td>{data.address}, {data.city}, {data.state}</td>
                  <td>{data.phonenumber || "N/A"}</td>
                  <td>{data.is_suspended ? "Suspended" : "Active"}</td>
                  <td className="">
                    <div className={classNames(styles.IconsContainer)}>
                      <CustomToolTip label="View">
                        <div
                          onClick={() => {
                            window.open(
                              `https://isopowersportsparts.com/general/seller/${data.id}`,
                              "_blank"
                            );
                          }}
                          className={classNames(styles.actionContainer)}
                        >
                          <Icons.Eye />
                        </div>
                      </CustomToolTip>
                      {data.is_suspended ? (
                        <CustomToolTip label="Activate">
                          <div
                            onClick={() => {
                              setSelectedUser(data);
                              handleShowConfirmationModal();
                            }}
                            className={classNames(styles.actionContainer)}
                          >
                            <Icons.Tick className={classNames(styles.accept)} />
                          </div>
                        </CustomToolTip>
                      ) : (
                        <CustomToolTip label="Suspend">
                          <div
                            onClick={() => {
                              setSelectedUser(data);
                              handleShowConfirmationModal();
                            }}
                            className={classNames(styles.actionContainer)}
                          >
                            <Icons.Cross />
                          </div>
                        </CustomToolTip>
                      )}
                      {data.flagged ? (
                        <CustomToolTip label="Unflag">
                          <div
                            onClick={() => {
                              setSelectedUser(data);
                              handleShowFlagModal();
                            }}
                            className={classNames(styles.actionContainer)}
                          >
                            <Icons.FlagFill />
                          </div>
                        </CustomToolTip>
                      ) : (
                        <CustomToolTip label="Flag">
                          <div
                            onClick={() => {
                              setSelectedUser(data);
                              handleShowFlagModal();
                            }}
                            className={classNames(styles.actionContainer)}
                          >
                            <Icons.FlagOutline />
                          </div>
                        </CustomToolTip>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
        </CustomTable>
      </div>

      <CustomPagination
        onPageChange={handlePageChange}
        totalCount={totalItems}
        currentPage={currentPage}
        pageSize={10}
      />

      <ConfirmationModal
        show={showConfirmationModal}
        handleClose={handleCloseConfirmationModal}
        handleSubmit={() => {
          handleSuspendUser();
        }}
        loading={suspendLoading}
      />

      <FlagUserModal
        show={showFlagModal}
        handleClose={handleCloseFlagModal}
        handleGetAllUsers={handleGetAllUsers}
        currentPage={currentPage}
        user={selectedUser}
      />
    </div>
  );
};

const TableRowLoader = () => (
  <tr>
    {Array.from({ length: 6 }).map((_, index) => (
      <td
        key={index}
        className={classNames(styles.tableContentLabel, "px-2 py-3")}
      >
        <div className="flex items-center justify-center">
          <SkeletonLoader className={styles.lineLoader} />
        </div>
      </td>
    ))}
  </tr>
);

export default DashboardUsers;
